import type { ConversationAlert } from '@quromedical/communication';
import { getReferenceId } from '@quromedical/fhir-common';
import { groupby, orderby } from '@quromedical/utils';
import { secondsToMilliseconds } from 'date-fns';
import { ActionList, SkeletonProvider } from 'design-system';
import { router } from 'expo-router';
import { dateTimeWithoutYearFormatter } from 'helpers';
import { useCommunicationAlerts } from 'hooks/useCommunicationAlerts';
import React, { useCallback } from 'react';
import { strings } from 'strings';

interface ListProps {
  isLoading: boolean;
  items: Required<ConversationAlert>[];
  onPatientPress: (id: string) => void;
  header?: boolean;
}

interface Updated {
  updated: number;
}

const List: React.FC<ListProps> = ({ isLoading, items, onPatientPress }) => {
  const groupedByPatient = groupby(
    items,
    (item) => getReferenceId(item.patient) || '',
  );
  const keys = Object.keys(groupedByPatient);
  const alertList = keys.map((patientId) => {
    const patientItems = groupedByPatient[patientId];

    const sortedItems = orderby(patientItems, (item) => item.sent, 'asc');
    const firstAlert = sortedItems[0];

    return {
      count: sortedItems.length,
      firstDateTime: firstAlert.sent,
      patient: firstAlert.patient,
    };
  });
  const orderedAlerts = orderby(alertList, (item) => item.firstDateTime, 'asc');

  return (
    <SkeletonProvider loading={isLoading}>
      <ActionList.List
        title={strings.CommunicationAlertTitle}
        icon="mark-unread-chat-alt"
        blockColor={'accent-green'}
      >
        <ActionList.SubHeading
          subtitle={strings.CommunicationAlertSubHeading}
          title={strings.TitlePatient}
          time={true}
        />
        {orderedAlerts.map((item) => {
          const { patient, firstDateTime, count } = item;

          const id = getReferenceId(patient) || '';
          const display = patient.display || strings.DisplayNotFound;

          // use the oldest time to indicate when the measurement was taken
          // const time = minby(latestMeasures, (v) => v?.ts);
          const dateTime = dateTimeWithoutYearFormatter(
            new Date(firstDateTime).getTime(),
          );

          return (
            <ActionList.Item
              key={id}
              id={id}
              firstColumn={dateTime}
              subTitle={count.toString()}
              title={display}
              onPress={onPatientPress}
              statusIcons={[
                {
                  name: 'mark-unread-chat-alt',
                  color: 'accent-green',
                },
              ]}
            />
          );
        })}
      </ActionList.List>
    </SkeletonProvider>
  );
};

/**
 * Since there could be many patients' data being updated at the same time, we use a memo to reduce
 * frequent renders
 */
const ListMemo = React.memo<ListProps & Updated>(List, (prev) => {
  const age = Date.now() - prev.updated;
  // using a relatively short memo time since the component isn't very heavy to render
  const stale = age > secondsToMilliseconds(3);
  return !stale;
});

interface CommunicationAlertListProps {
  remove?: boolean;
}

export const CommunicationAlertList: React.FC<
  CommunicationAlertListProps
> = () => {
  const onPatientPress = useCallback(
    (id: string) =>
      router.push({
        pathname: `/patient/${id}/communication`,
      }),
    [],
  );

  const { data, isLoading } = useCommunicationAlerts();

  return (
    <ListMemo
      isLoading={isLoading}
      updated={0}
      items={data || []}
      onPatientPress={onPatientPress}
    />
  );
};
